import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { adminMothersApi } from '../../lib/api';
import { toast } from 'sonner';
import { Heart, Search, ChevronRight, Mail, Phone, Calendar } from 'lucide-react';

const formatDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('tr-TR', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return iso; }
};

export const MothersPage = () => {
  const [mothers, setMothers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await adminMothersApi.list({ q: searchTerm.trim() || undefined, limit: 100 });
        setMothers(res.mothers || []);
        setTotal(res.total || 0);
      } catch {
        toast.error('Anne listesi yüklenemedi');
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [searchTerm]);

  return (
    <DashboardLayout title="Anneler" subtitle="B2C kullanıcıları — tüm hamile kullanıcılar">
      <div className="space-y-4" data-testid="admin-mothers-page">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                placeholder="İsim, e-posta veya telefon ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="admin-mothers-search-input"
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex items-baseline justify-between text-sm text-slate-500 px-1">
          <span>Toplam: <strong className="text-slate-800">{total}</strong> anne</span>
        </div>

        {loading ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-8 text-center text-slate-400">Yükleniyor...</CardContent>
          </Card>
        ) : mothers.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-8 text-center text-slate-400" data-testid="admin-mothers-empty">
              {searchTerm ? 'Eşleşen anne bulunamadı' : 'Henüz kayıtlı anne yok'}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {mothers.map((m) => (
              <Link
                key={m.id}
                to={`/superadmin/mothers/${m.id}`}
                className="block group"
                data-testid={`admin-mother-row-${m.id}`}
              >
                <Card className="border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FB7185] to-[#F43F5E] flex items-center justify-center flex-shrink-0 text-white">
                      <Heart className="w-5 h-5" fill="currentColor" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-semibold text-slate-800 truncate">{m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || '—'}</p>
                        {m.pregnancy_week && (
                          <span className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full font-medium">{m.pregnancy_week}. hafta</span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-slate-500 mt-1 flex-wrap">
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{m.email}</span>
                        {m.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{m.phone}</span>}
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(m.created_at)}</span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 group-hover:text-[#FB7185] transition-colors" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default MothersPage;
